import { env } from '@bitstopco/bitstop-theme';
import type { History } from 'history';

import { getOtelConfig, redactPath } from './config';

type OtelSdk = typeof import('./sdk');
type QueuedCall = (sdk: OtelSdk) => void;

// Errors and route changes that happen before the SDK chunk has loaded are kept and replayed.
const MAX_QUEUED_CALLS = 50;

let enabled = false;
let sdk: OtelSdk | undefined;
let queue: QueuedCall[] = [];

const callSdk = (call: QueuedCall) => {
  if (!enabled) {
    return;
  }

  if (sdk) {
    call(sdk);
  } else if (queue.length < MAX_QUEUED_CALLS) {
    queue.push(call);
  }
};

const toError = (value: unknown): Error => {
  if (value instanceof Error) {
    return value;
  }

  if (typeof value === 'string') {
    return new Error(value);
  }

  try {
    return new Error(JSON.stringify(value));
  } catch {
    return new Error(String(value));
  }
};

/**
 * Sends an error to SigNoz as an errored span (shows under Exceptions) and an ERROR log record.
 * A no-op while OpenTelemetry is disabled.
 */
export const recordError = (value: unknown, source: string) => {
  if (!enabled) {
    return;
  }

  const error = toError(value);
  const path = redactPath(window.location.pathname);
  callSdk((otel) => otel.recordError(error, source, path));
};

/**
 * Records a span for every route change. Only pathname changes count; query-string updates
 * (table filters, pagination) are not navigations. A no-op while OpenTelemetry is disabled.
 */
export const trackRouteChanges = (history: History) => {
  if (!enabled) {
    return;
  }

  let previousPath = history.location.pathname;

  history.listen(({ action, location }) => {
    if (location.pathname === previousPath) {
      return;
    }

    // Reported redacted (ids replaced) so customer/machine ids never leave the browser; compared
    // raw so /customers/1 -> /customers/2 still counts as a navigation.
    const path = redactPath(location.pathname);
    const from = redactPath(previousPath);
    callSdk((otel) => otel.recordRouteChange(path, from, action));
    previousPath = location.pathname;
  });
};

/**
 * Starts browser OpenTelemetry (traces, metrics and logs exported to the SigNoz collector over
 * OTLP/HTTP). Does nothing unless enabled through the `VITE_OTEL_*` env vars, see `getOtelConfig`,
 * and never runs under Cypress. The SDK itself is a separate chunk that is only downloaded here.
 */
export const initOtel = () => {
  if (enabled || typeof window === 'undefined' || window.Cypress) {
    return;
  }

  const config = getOtelConfig(import.meta.env, env.isTestnet());
  if (!config) {
    return;
  }

  enabled = true;

  window.addEventListener('error', (event) => {
    recordError(event.error ?? event.message, 'window.error');
  });
  window.addEventListener('unhandledrejection', (event) => {
    recordError(event.reason, 'window.unhandledrejection');
  });

  import('./sdk')
    .then((otel) => {
      otel.startOtel(config);
      sdk = otel;
      for (const call of queue) {
        call(otel);
      }
      queue = [];
    })
    .catch(() => {
      // Telemetry must never break the app; if the chunk fails to load, stay disabled.
      enabled = false;
      queue = [];
    });
};
